import { CalendarCheck, Globe2, MapPin, Percent, TrendingUp, Users, Wallet } from 'lucide-react';
import React from 'react';

import { useMarketingFieldVisibility } from '../use-marketing-reports';

import type { MarketingChannelMonth } from '@utazzo/shared';

import { KpiCard, KpiGrid } from '@/features/reports/components/KpiCard';

const inr = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-IN');
  }
};

const DASH = '-';
const count = (value: number): string => value.toLocaleString('en-IN');
const pct = (value: number | null): string => (value === null ? DASH : `${value.toFixed(2)}%`);
const money = (value: number | null): string => (value === null ? DASH : inr(value));

const shimmer = (
  <span className="bg-muted inline-block h-6 w-14 animate-pulse rounded align-middle" />
);

/**
 * Headline numbers for the selected channel, for the newest month in range.
 *
 * Scoped to ONE month (named in every hint) for the same reason the Overview
 * tiles are: summing conversion and order value across months would blend
 * periods and read as a single month's performance.
 */
export const ChannelKpis = ({
  channelName,
  month,
  monthLabel,
  loading,
}: {
  channelName: string;
  month: MarketingChannelMonth | undefined;
  monthLabel: string | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const { isHidden } = useMarketingFieldVisibility();
  const hint = monthLabel ? `${channelName} · ${monthLabel}` : channelName;
  const value = (render: (m: MarketingChannelMonth) => string): React.ReactNode =>
    loading || !month ? shimmer : render(month);

  return (
    <KpiGrid>
      <KpiCard
        icon={Users}
        tone="primary"
        value={value((m) => count(m.overall.query))}
        label="Query"
        hint={hint}
      />
      <KpiCard
        icon={CalendarCheck}
        tone="success"
        value={value((m) => count(m.overall.converted))}
        label="Converted"
        hint={hint}
      />
      {!isHidden('conversionPct') && (
        <KpiCard
          icon={Percent}
          value={value((m) => pct(m.overall.conversionPct))}
          label="% Conversion"
          hint={hint}
        />
      )}
      {!isHidden('salesProfit') && (
        <KpiCard
          icon={TrendingUp}
          tone="success"
          value={value((m) => money(m.salesProfit))}
          label="Profit (Sales Tracker)"
          hint={hint}
        />
      )}
      {!isHidden('avgOrderValue') && (
        <KpiCard
          icon={Wallet}
          value={value((m) => money(m.overall.avgOrderValue))}
          label="Avg Order Value"
          hint={hint}
        />
      )}
      <KpiCard
        icon={Globe2}
        tone="primary"
        value={value((m) => count(m.international.query))}
        label="International Query"
        hint={value(
          (m) => `${m.international.converted} won · ${pct(m.international.conversionPct)}`,
        )}
      />
      <KpiCard
        icon={MapPin}
        tone="warning"
        value={value((m) => count(m.domestic.query))}
        label="Domestic Query"
        hint={value((m) => `${m.domestic.converted} won · ${pct(m.domestic.conversionPct)}`)}
      />
    </KpiGrid>
  );
};
